import posthog from "posthog-js";

// Read from the existing environment config (GitLab CI injects these into
// .env.local before the production build). No key is ever hardcoded or
// committed. MEE-942 owns provisioning the per-product key.
const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST;

// The free plan allows one project for the whole organisation, so every event
// carries the product it came from.
const PRODUCT = "dashboard-react";

// Local development and the test runner must never send data to PostHog. The
// production build is the only place NODE_ENV is "production", so this keeps
// dev noise out of the data entirely. Both values must be configured; there is
// no default host, so a half-configured environment sends nothing.
export const isPostHogEnabled = () =>
  Boolean(POSTHOG_KEY && POSTHOG_HOST) && process.env.NODE_ENV === "production";

export const initPostHog = () => {
  if (!isPostHogEnabled()) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Off deliberately. Autocapture records the text of whatever was clicked,
    // which on this dashboard is customer names and order values. Error
    // tracking and session replay do not need it.
    autocapture: false,
    // React Router drives navigation through history.pushState, which the
    // default pageview listener does not see. history_change patches the
    // history API so every in-app route change is captured as a pageview.
    capture_pageview: "history_change",
    capture_pageleave: true,
    // Uncaught exceptions and unhandled promise rejections.
    capture_exceptions: true,
    // Session replay is on, but every input is masked so a staff member typing
    // a customer name, phone, email or card number is never stored. Text and
    // images rendered on the page are covered by the project's own masking
    // level, which is set to total privacy (MEE-942).
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
    },
  });

  posthog.register({ product: PRODUCT });
};

const toId = (value) => (
  value === undefined || value === null ? undefined : String(value)
);

// Identify the logged-in user by their existing user id and org id only. Names
// and emails are deliberately never sent. The org id is attached both as a
// person property and as a group so reports can say which brand and which
// staff member hit the error.
export const identifyPostHogUser = (userInfo) => {
  if (!isPostHogEnabled() || !userInfo) return;

  const userId = toId(userInfo.id);
  const orgId = toId(userInfo.organization_id ?? userInfo.organizationId);

  if (userId) {
    posthog.identify(userId, orgId ? { organization_id: orgId } : undefined);
  }
  if (orgId) {
    posthog.group("organization", orgId);
  }
};

export const resetPostHog = () => {
  if (!isPostHogEnabled()) return;
  posthog.reset();
};
